import React, { AriaAttributes, ReactNode } from 'react';
import { ChevronDown, ChevronUp } from '@bloomberg/media-ui-icons';

import { useSlug } from '../hooks/useSlug';
import type { GlobalProps } from '../types';
import { classes } from '../utils';

import styles from './DropdownButton.module.css';

export type DropdownButtonProps = GlobalProps & {
  icon?: ReactNode;
  id?: string;
  text?: string;
  onClick: () => void;
  onKeyDown?: () => void;
  onBlur?: () => void;
  tabIndex?: number;
  role?: string;
  'aria-controls'?: AriaAttributes['aria-controls'];
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
  'aria-labelledby'?: AriaAttributes['aria-labelledby'];
  isOpen?: boolean;
};

export const DropdownButton = React.forwardRef<
  HTMLButtonElement,
  DropdownButtonProps
>(function DropdownButtonComponent(props: DropdownButtonProps, ref) {
  const {
    icon,
    text,
    name = 'DropdownButton',
    onClick,
    unstyled,
    id,
    onKeyDown,
    onBlur,
    tabIndex,
    role,
    'aria-controls': ariaControls,
    'aria-labelledby': ariaLabelledBy,
    'aria-haspopup': ariaHasPopup,
    isOpen,
  } = props;

  const componentName = useSlug(name);

  const cx = classes([styles.DropdownButton], {
    ...props,
    unstyled,
    name,
  });

  return (
    <button
      id={id}
      className={cx}
      ref={ref}
      data-component={componentName}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      tabIndex={tabIndex}
      aria-controls={ariaControls}
      aria-labelledby={ariaLabelledBy}
      aria-haspopup={ariaHasPopup}
      aria-label={text ?? 'Open'}
      aria-expanded={isOpen}
      role={role}
    >
      {icon && <span className={styles.leftIcon}>{icon}</span>}
      <p className={styles.text}>{text}</p>
      <span className={styles.dropdownIcon}>
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </span>
    </button>
  );
});
